import {useContext} from '@/compat';
import {computed, ref} from 'vue';
import useDebounce from '../useDebounce';

// Sayacın üst sınırı: sunucu sayımı bu eşikte durdurur, UI "10.000+" gösterir.
// Canlı yenisafak'ta filtresiz sayım ~1,9M kaydı tarayıp ~2 s sürüyordu; eşik
// sayacı sabit maliyete indirir. Filtreli (daha dar) listelerde tam sayı gelir.
export const TOTAL_COUNT_LIMIT = 10000;

/**
 * @param {object} params
 * @param {function} params.fetch (page, itemsPerPage) => Promise<Array>
 * @param {number} params.itemsPerPage
 * @param {object} [params.debounce]
 * @param {function} [params.fetchCount] Opsiyonel; ({ limit }) => Promise<number>. Verilirse ilk
 * sayfa her yüklendiğinde (ilk açılış, filtre/arama değişimi) liste ile aynı filtre için toplam
 * kayıt sayısını getirir. Sayaç listeyi BEKLETMEZ: liste isteği tamamlanınca tablo çizilir, sayı
 * arkadan gelir. Sonuç `totalCount` / `isTotalCountLoading` / `isTotalCountCapped` üzerinden
 * okunur; hata durumunda null kalır.
 * @param {number} [params.countLimit] Sayımın üst sınırı; varsayılan TOTAL_COUNT_LIMIT.
 */
const useInfiniteFetch = ({
  fetch, itemsPerPage, debounce = { delay: 0, immediate: true }, fetchCount = null, countLimit = TOTAL_COUNT_LIMIT,
}) => {
  const { $i18n, $lang } = useContext();

  const data = ref([]);
  const page = ref(1);

  const isFetching = ref(true);
  const isAllLoaded = ref(false);
  const noData = ref(false);

  const totalCount = ref(null);
  const isTotalCountLoading = ref(false);
  const isTotalCountCapped = computed(() => totalCount.value !== null && totalCount.value >= countLimit);
  // Filtre hızlı değişince eski sayım sonucu yeni listenin üstüne yazmasın.
  let totalCountRequestId = 0;

  const loadTotalCount = async () => {
    totalCountRequestId += 1;
    const requestId = totalCountRequestId;

    totalCount.value = null;
    isTotalCountLoading.value = true;

    let count = null;

    try {
      const fetched = await fetchCount({ limit: countLimit });

      count = Number.isFinite(fetched) ? fetched : null;
    } catch (e) {
      count = null;
    }

    // Bu arada daha yeni bir sayım başladıysa eski sonuç yazılmaz.
    if (requestId !== totalCountRequestId) {
      return;
    }

    totalCount.value = count;
    isTotalCountLoading.value = false;
  };

  const progressedFetch = async () => {
    // Sayaç, liste isteğiyle aynı debounce içinde ve yalnızca ilk sayfada tetiklenir;
    // "daha fazla göster" sayacı değiştirmez. Beklenmez: liste sayaçtan bağımsız gelir.
    if (fetchCount && page.value === 1) {
      loadTotalCount();
    }

    return await fetch(page.value, itemsPerPage);
  };

  const debouncedProgressedFetch = useDebounce({ delay: debounce.delay, immediate: debounce.immediate }, progressedFetch);

  const fetchData = async (immediate = false) => {
    isFetching.value = true;
    noData.value = false;
    const fetchedData = (await (immediate ? progressedFetch() : debouncedProgressedFetch())) || [];
    data.value = page.value === 1 ? fetchedData : data.value.concat(fetchedData);

    if (fetchedData.length < itemsPerPage) {
      isAllLoaded.value = true;
    }

    if (data.value.length === 0) {
      noData.value = true;
    }

    isFetching.value = false;
  };

  const reFetch = async (immediate = false) => {
    page.value = 1;
    isAllLoaded.value = false;
    await fetchData(immediate);
  };

  const showMore = async () => {
    page.value += 1;
    await fetchData(true);
  };

  const showMoreButtonText = computed(() => {
    if (noData.value) {
      return $i18n.t($lang.app.NO_DATA_AVAILABLE);
    }

    return isAllLoaded.value
      ? $i18n.t($lang.app.ALL_DATA_LOADED)
      : $i18n.t($lang.app.SHOW_MORE);
  });

  const showMoreButtonIcon = computed(() => {
    if (noData.value) {
      return 'mdi-playlist-remove';
    }

    return isAllLoaded.value
      ? 'mdi-check-circle'
      : 'mdi-dots-vertical';
  });

  const isLoadingFirstPage = computed(() => page.value === 1 && isFetching.value);

  const dataLoadParameters = computed(() => ({
    isFetching,
    isAllLoaded: computed(() => isAllLoaded.value),
    showMoreButton: {
      text: showMoreButtonText,
      icon: showMoreButtonIcon,
    },
    isLoadingFirstPage,
    totalCount,
    isTotalCountLoading,
    isTotalCountCapped,
  }));

  return {
    page,
    data,
    totalCount,
    isTotalCountLoading,
    isTotalCountCapped,
    fetch: fetchData,
    reFetch,
    showMore,
    dataLoadParameters,
  };
};

export default useInfiniteFetch;
